import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';
import {
    clampProgressPercentage,
    getOkrSubmissionState
} from './operatorDashboardOkrSubmission';
import {
    getCompletedPreventivesFromOkrResults,
    getOkrInlineDetails
} from './operatorDashboardOkrDetails';
import {
    approveOperatorAnomalyReview,
    completeOperatorProductReturn,
    getPendingOperatorAnomalyReviews,
    getOperatorDashboardOkrContext,
    getOperatorDashboardOkrWeekly,
    getOperatorDashboardSummary,
    getOperatorReturnTickets,
    rejectOperatorAnomalyReview,
    returnOperatorProductToSupport,
    saveAndSubmitOperatorAnomalyReview,
    submitOperatorDashboardOkrUpdate
} from './operatorDashboardApi';
import './operatorDashboard.css';
import OperatorSupervision from './OperatorSupervision';
import CoffeeRegistration from '../CoffeeStatistics/CoffeeRegistration';
import { canAccessCoffeeRegistration } from '../../navigation/moduleAccess';

const formatDateForApi = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

const parseOkrDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const isoDate = String(value).slice(0, 10);
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const formatLocalDateTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
};

const firstText = (...values) =>
    values.find((value) => value !== null && value !== undefined && String(value).trim() !== '') || '';

const formatDisplayTime = (value) => {
    if (!value || value === 'N/A' || value === '-') return value || '-';
    const raw = String(value).trim();
    if (/\b[AP]M\b/i.test(raw)) return raw.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
    const match = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return raw;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour > 23 || minute > 59) return raw;
    const hour12 = hour % 12 || 12;
    const meridiem = hour < 12 ? 'AM' : 'PM';
    return `${hour12}:${String(minute).padStart(2, '0')} ${meridiem}`;
};

const formatTimeRange = (from, to, fallback = null) => {
    const fromDisplay = formatDisplayTime(from);
    const toDisplay = formatDisplayTime(to);
    if (fromDisplay && toDisplay && fromDisplay !== '-' && toDisplay !== '-') {
        return `${fromDisplay} → ${toDisplay}`;
    }
    return formatDisplayTime(fallback);
};

const formatDetailRange = (from, to, fallback = null) => {
    const fromDisplay = firstText(from);
    const toDisplay = firstText(to);
    if (fromDisplay && toDisplay) return `${fromDisplay} → ${toDisplay}`;
    return firstText(fallback, '-');
};

const numberValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) =>
    numberValue(value).toLocaleString('es-PA', { maximumFractionDigits: 2 });

const safeParseStorage = (key) => {
    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch {
        return null;
    }
};

const getUserFromPayload = (payload) =>
    payload?.data?.adminUser ||
    payload?.data?.user ||
    payload?.adminUser ||
    payload?.user ||
    (payload && (payload._id || payload.id || payload.email || payload.firstName || payload.lastName || payload.name)
        ? payload
        : null) ||
    null;

const getCurrentAdminUser = () =>
    getUserFromPayload(safeParseStorage('userDetails')) ||
    getUserFromPayload(safeParseStorage('adminUser')) ||
    {};

const getId = (value) => value?._id || value?.id || value || '';
const sameId = (left, right) => String(getId(left)) === String(getId(right));
const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const normalizePersonName = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

const getUserDisplayName = (user = {}) =>
    user.name ||
    user.fullName ||
    `${user.firstName || user.firstname || ''} ${user.lastName || user.lastname || ''}`.trim() ||
    user.displayName ||
    '';

const userMatchesCollaborator = (user = {}, collaborator = {}) => {
    if (!user || !collaborator) return false;
    if (getId(user) && getId(collaborator) && sameId(user, collaborator)) return true;
    const userEmail = normalizeEmail(user.email);
    const collaboratorEmail = normalizeEmail(collaborator.email);
    if (userEmail && collaboratorEmail && userEmail === collaboratorEmail) return true;
    const userName = normalizePersonName(getUserDisplayName(user));
    const collaboratorName = normalizePersonName(collaborator.name || getUserDisplayName(collaborator));
    return Boolean(userName && collaboratorName && userName === collaboratorName);
};

const getCurrentUserCollaboratorId = (collaborators = [], user = {}) => {
    const matches = (Array.isArray(collaborators) ? collaborators : []).filter((collaborator) =>
        userMatchesCollaborator(user, collaborator)
    );
    return matches.length === 1 ? matches[0].id : '';
};

const getStatusSeverity = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'submitted') return 'info';
    if (status === 'draft') return 'warning';
    if (status === 'rejected') return 'danger';
    return 'secondary';
};

const OperatorDashboard = ({ user, dashboardAccess = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dashboardView, setDashboardView] = useState('individual');
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const t = useCallback((value) => translateUiText(value, preferredLanguage), [preferredLanguage]);

    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const [okrLoading, setOkrLoading] = useState(true);
    const [okrWeeklyLoading, setOkrWeeklyLoading] = useState(false);
    const [okrError, setOkrError] = useState(null);
    const [okrContext, setOkrContext] = useState(null);
    const [selectedCollaboratorId, setSelectedCollaboratorId] = useState('');
    const [selectedOkrWeek, setSelectedOkrWeek] = useState(null);
    const [okrWeekly, setOkrWeekly] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [summary, setSummary] = useState(null);
    const [reviewStatusFilter, setReviewStatusFilter] = useState('');
    const [reviewDialogVisible, setReviewDialogVisible] = useState(false);
    const [selectedAnomaly, setSelectedAnomaly] = useState(null);
    const [reviewExplanation, setReviewExplanation] = useState('');
    const [reviewError, setReviewError] = useState(null);
    const [reviewSaving, setReviewSaving] = useState(false);
    const [pendingReviews, setPendingReviews] = useState([]);
    const [pendingReviewsLoading, setPendingReviewsLoading] = useState(false);
    const [returnTickets, setReturnTickets] = useState({ total: 0, productDeliveries: 0, bankRefunds: 0, items: [] });
    const [returnTicketsLoading, setReturnTicketsLoading] = useState(false);
    const [returnTicketsError, setReturnTicketsError] = useState(null);
    const [returnDialog, setReturnDialog] = useState({ visible: false, ticket: null });
    const [returnNote, setReturnNote] = useState('');
    const [returnSaving, setReturnSaving] = useState(false);
    const [supportReturnDialog, setSupportReturnDialog] = useState({ visible: false, ticket: null });
    const [supportReturnReason, setSupportReturnReason] = useState('');
    const [supportReturnSaving, setSupportReturnSaving] = useState(false);
    const [decisionDialog, setDecisionDialog] = useState({ visible: false, action: '', review: null });
    const [decisionComment, setDecisionComment] = useState('');
    const [decisionSaving, setDecisionSaving] = useState(false);
    const [submittingEntryId, setSubmittingEntryId] = useState(null);
    const [mainTabIndex, setMainTabIndex] = useState(0);
    const [anomalyTabIndex, setAnomalyTabIndex] = useState(0);

    const currentAdminUser = useMemo(() => user || getCurrentAdminUser(), [user]);
    const canViewCoffee = canAccessCoffeeRegistration(currentAdminUser);
    const coffeeTabIndex = 2;
    const coffeeActive = canViewCoffee && mainTabIndex === coffeeTabIndex;
    const openDashboardView = (view) => {
        setDashboardView(view);
        const params = new URLSearchParams(location.search);
        if (view === 'coffee') params.set('tab', 'coffee');
        else params.delete('tab');
        navigate({ pathname: location.pathname, search: params.toString() });
    };
    const currentRoleUid = typeof currentAdminUser?.role === 'string'
        ? currentAdminUser.role
        : currentAdminUser?.role?.uid || currentAdminUser?.roleUid;
    const isAdminReviewer = currentRoleUid === 'admin' || currentRoleUid === 'superAdmin';

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tab') === 'coffee' && canViewCoffee) setMainTabIndex(coffeeTabIndex);
        else if (!dashboardAccess && canViewCoffee) setMainTabIndex(coffeeTabIndex);
        else if (params.get('tab') === 'anomalies') setMainTabIndex(1);
        else if (params.get('tab') === 'returns') setMainTabIndex(3);
        else setMainTabIndex(0);
        if (params.get('section') === 'schedule') setAnomalyTabIndex(1);
    }, [location.search, canViewCoffee, coffeeTabIndex, dashboardAccess]);

    useEffect(() => {
        if (!returnTicketsLoading && returnTickets.productDeliveries === 0 && mainTabIndex === 3) {
            setMainTabIndex(0);
        }
    }, [mainTabIndex, returnTickets.productDeliveries, returnTicketsLoading]);

    useEffect(() => {
        const sync = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('preferred-language-changed', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    const selectedCollaborator = useMemo(
        () => (okrContext?.collaborators || []).find((collaborator) => collaborator.id === selectedCollaboratorId) || null,
        [okrContext, selectedCollaboratorId]
    );

    const currentUserCollaboratorId = useMemo(
        () => getCurrentUserCollaboratorId(okrContext?.collaborators || [], currentAdminUser),
        [currentAdminUser, okrContext]
    );

    const selectedOkrWeekDetail = useMemo(
        () => (okrContext?.weeks || []).find((week) => Number(week.number) === Number(selectedOkrWeek)) || null,
        [okrContext, selectedOkrWeek]
    );

    const collaboratorOptions = useMemo(
        () =>
            (okrContext?.collaborators || []).map((collaborator) => ({
                label: collaborator.name || collaborator.email,
                value: collaborator.id
            })),
        [okrContext]
    );

    const canAccessAllOperators = Boolean(okrContext?.accessScope?.canAccessAllOperators);
    const canViewSupervision = isAdminReviewer || Boolean(okrContext?.reviewPermissions?.canViewSupervision);

    const okrWeekOptions = useMemo(
        () =>
            (okrContext?.weeks || []).map((week) => ({
                label: Number(week.number) === Number(okrContext?.cycle?.currentWeek)
                    ? `${t('Week')} ${week.number} - ${t('Current week')}`
                    : `${t('Week')} ${week.number}`,
                value: week.number
            })),
        [okrContext, t]
    );

    const applyOkrWeekDates = useCallback((weekNumber, weeks = []) => {
        const week = (weeks || []).find((item) => Number(item.number) === Number(weekNumber));
        const startDate = parseOkrDate(week?.startDate);
        const endDate = parseOkrDate(week?.endDate);
        if (!startDate || !endDate) return;
        setFromDate(startDate);
        setToDate(endDate);
    }, []);

    const loadOkrContext = useCallback(async () => {
        setOkrLoading(true);
        setOkrError(null);
        setOkrWeekly(null);
        try {
            const response = await getOperatorDashboardOkrContext();
            setOkrContext(response);
            const weeks = Array.isArray(response?.weeks) ? response.weeks : [];
            const currentWeek = response?.cycle?.currentWeek;
            const currentExists = weeks.some((week) => Number(week.number) === Number(currentWeek));
            const nextWeek = currentExists ? currentWeek : weeks.length ? weeks[weeks.length - 1].number : null;
            const collaborators = Array.isArray(response?.collaborators) ? response.collaborators : [];
            const currentUserCollaboratorId = getCurrentUserCollaboratorId(collaborators, currentAdminUser);
            setSelectedOkrWeek(nextWeek);
            applyOkrWeekDates(nextWeek, weeks);
            setSelectedCollaboratorId((currentId) =>
                collaborators.some((collaborator) => collaborator.id === currentId)
                    ? currentId
                    : currentRoleUid === 'operator' && currentUserCollaboratorId
                    ? currentUserCollaboratorId
                    : ''
            );
        } catch (error) {
            setOkrContext(null);
            setSelectedCollaboratorId('');
            setSelectedOkrWeek(null);
            setOkrError(error);
        } finally {
            setOkrLoading(false);
        }
    }, [applyOkrWeekDates, currentAdminUser, currentRoleUid]);

    useEffect(() => {
        if (dashboardAccess) loadOkrContext();
        else setOkrLoading(false);
    }, [loadOkrContext, dashboardAccess]);

    const validateFilters = useCallback(() => {
        if (!selectedCollaboratorId) return t('Select an operator to view their operation and weekly OKR results.');
        const from = formatDateForApi(fromDate);
        const to = formatDateForApi(toDate);
        if (!from || !to) return t('Please select a valid date range');
        if (fromDate > toDate) return t('From date cannot be after to date.');
        return null;
    }, [selectedCollaboratorId, fromDate, toDate, t]);

    const loadSummary = useCallback(async () => {
        const validationMessage = validateFilters();
        if (validationMessage) {
            setSummaryError(new Error(validationMessage));
            return;
        }

        setSummaryLoading(true);
        setSummaryError(null);
        try {
            const response = await getOperatorDashboardSummary({
                collaboratorId: selectedCollaboratorId,
                from: formatDateForApi(fromDate),
                to: formatDateForApi(toDate)
            });
            setSummary(response);
        } catch (error) {
            setSummary(null);
            setSummaryError(error);
        } finally {
            setSummaryLoading(false);
        }
    }, [selectedCollaboratorId, fromDate, toDate, validateFilters]);

    const loadOkrWeekly = useCallback(async () => {
        if (!selectedCollaboratorId || !okrContext?.cycle?.id || !selectedOkrWeek) {
            setOkrWeekly(null);
            return;
        }

        setOkrWeeklyLoading(true);
        setOkrError(null);
        try {
            const response = await getOperatorDashboardOkrWeekly({
                collaboratorId: selectedCollaboratorId,
                cycleId: okrContext.cycle.id,
                weekNumber: selectedOkrWeek
            });
            setOkrWeekly(response);
        } catch (error) {
            setOkrWeekly(null);
            setOkrError(error);
        } finally {
            setOkrWeeklyLoading(false);
        }
    }, [selectedCollaboratorId, okrContext, selectedOkrWeek]);

    const canReviewAnomalies = Boolean(okrContext?.reviewPermissions?.canReviewAnomalies) || isAdminReviewer;

    const getSubmissionState = useCallback((result) => getOkrSubmissionState({
        roleUid: currentRoleUid,
        selectedCollaboratorId,
        currentUserCollaboratorId,
        week: selectedOkrWeekDetail,
        result
    }), [currentRoleUid, selectedCollaboratorId, currentUserCollaboratorId, selectedOkrWeekDetail]);

    const handleSubmitOkrUpdate = async (result) => {
        const submissionState = getSubmissionState(result);
        if (!submissionState.canSubmit || !result?.entryId) return;
        setSubmittingEntryId(result.entryId);
        try {
            await submitOperatorDashboardOkrUpdate(result.entryId);
            toast.success(t('Weekly update sent.'));
            await loadOkrWeekly();
        } catch (error) {
            toast.error(error?.message || t('Could not send the weekly update.'));
        } finally {
            setSubmittingEntryId(null);
        }
    };

    const loadPendingReviews = useCallback(async () => {
        if (!canReviewAnomalies || !selectedCollaboratorId) {
            setPendingReviews([]);
            return;
        }
        setPendingReviewsLoading(true);
        try {
            const response = await getPendingOperatorAnomalyReviews();
            const reviews = Array.isArray(response?.reviews) ? response.reviews : [];
            setPendingReviews(
                reviews.filter((review) => sameId(review.collaborator, selectedCollaboratorId))
            );
        } catch {
            setPendingReviews([]);
        } finally {
            setPendingReviewsLoading(false);
        }
    }, [canReviewAnomalies, selectedCollaboratorId]);

    const loadReturnTickets = useCallback(async () => {
        if (!selectedCollaboratorId) {
            setReturnTickets({ total: 0, productDeliveries: 0, bankRefunds: 0, items: [] });
            setReturnTicketsError(null);
            return;
        }
        setReturnTicketsLoading(true);
        setReturnTicketsError(null);
        try {
            const response = await getOperatorReturnTickets({ collaboratorId: selectedCollaboratorId });
            setReturnTickets({
                total: numberValue(response?.total),
                productDeliveries: numberValue(response?.productDeliveries),
                bankRefunds: numberValue(response?.bankRefunds),
                items: Array.isArray(response?.items) ? response.items : []
            });
        } catch (error) {
            setReturnTickets({ total: 0, productDeliveries: 0, bankRefunds: 0, items: [] });
            setReturnTicketsError(error);
        } finally {
            setReturnTicketsLoading(false);
        }
    }, [selectedCollaboratorId]);

    useEffect(() => {
        if (selectedCollaboratorId && fromDate && toDate && fromDate <= toDate) {
            loadSummary();
        } else {
            setSummary(null);
        }
    }, [selectedCollaboratorId, fromDate, toDate, loadSummary]);

    useEffect(() => {
        if (!okrLoading) loadOkrWeekly();
    }, [okrLoading, selectedCollaboratorId, selectedOkrWeek, loadOkrWeekly]);

    useEffect(() => {
        if (!okrLoading) loadPendingReviews();
    }, [okrLoading, loadPendingReviews]);

    useEffect(() => {
        if (!okrLoading) loadReturnTickets();
    }, [okrLoading, loadReturnTickets]);

    const routeExecution = summary?.routeExecution || {};
    const todayExecution = routeExecution?.today && (
        numberValue(routeExecution.today.planned) > 0 ||
        numberValue(routeExecution.today.serviced) > 0
    ) ? routeExecution.today : null;
    const scorecard = summary?.scorecard || {};
    const okr2 = scorecard?.okr2 || scorecard?.kr2 || {};
    const completedMaintenance = getCompletedPreventivesFromOkrResults(
        okrWeekly?.results || [],
        okr2.completed
    );
    const anomalies = summary?.anomalies || {};
    const hasOperationalData =
        numberValue(routeExecution.planned) > 0 ||
        numberValue(routeExecution.serviced) > 0 ||
        numberValue(routeExecution.unserviced) > 0 ||
        numberValue(todayExecution?.planned) > 0 ||
        numberValue(todayExecution?.serviced) > 0 ||
        numberValue(completedMaintenance) > 0 ||
        (anomalies.unservicedMachines || []).length > 0 ||
        (anomalies.afterHours || []).length > 0 ||
        (anomalies.timeGaps || []).length > 0;

    const renderSummaryError = () => {
        if (!summaryError) return null;
        const message =
            summaryError.status === 403
                ? t('You do not have access to this operator dashboard.')
                : summaryError.message || t('Could not load operator dashboard summary.');
        return <Message severity={summaryError.status === 403 ? 'warn' : 'error'} text={message} />;
    };

    const unservicedMachines = useMemo(
        () => (Array.isArray(anomalies.unservicedMachines) ? anomalies.unservicedMachines : []),
        [anomalies.unservicedMachines]
    );
    const afterHours = useMemo(
        () => (Array.isArray(anomalies.afterHours) ? anomalies.afterHours : []),
        [anomalies.afterHours]
    );
    const timeGaps = useMemo(
        () => (Array.isArray(anomalies.timeGaps) ? anomalies.timeGaps : []),
        [anomalies.timeGaps]
    );
    const selectedReviewCollaborator = selectedCollaborator || summary?.collaborator || null;
    const canEditSelectedAnomalies = Boolean(selectedReviewCollaborator) && userMatchesCollaborator(currentAdminUser, selectedReviewCollaborator);

    const reviewStatusOptions = useMemo(
        () => [
            { label: t('All statuses'), value: '' },
            { label: t('Unjustified'), value: 'none' },
            { label: t('Draft'), value: 'draft' },
            { label: t('Pending approval'), value: 'submitted' },
            { label: t('Approved'), value: 'approved' },
            { label: t('Rejected'), value: 'rejected' }
        ],
        [t]
    );

    const getReviewStatusLabel = useCallback(
        (status) => {
            const labels = {
                none: t('Unjustified'),
                draft: t('Draft'),
                submitted: t('Pending approval'),
                approved: t('Approved'),
                rejected: t('Rejected')
            };
            return labels[status || 'none'] || t('Unjustified');
        },
        [t]
    );

    const getReviewSeverity = (status) => {
        if (status === 'approved') return 'success';
        if (status === 'submitted') return 'info';
        if (status === 'draft') return 'warning';
        if (status === 'rejected') return 'danger';
        return 'secondary';
    };

    const filterByReviewStatus = useCallback(
        (rows = []) =>
            rows.filter((row) => {
                if (!reviewStatusFilter) return true;
                const status = row?.review?.status || 'none';
                return status === reviewStatusFilter;
            }),
        [reviewStatusFilter]
    );

    const filteredUnservicedMachines = useMemo(() => filterByReviewStatus(unservicedMachines), [filterByReviewStatus, unservicedMachines]);
    const filteredAfterHours = useMemo(() => filterByReviewStatus(afterHours), [filterByReviewStatus, afterHours]);
    const filteredTimeGaps = useMemo(() => filterByReviewStatus(timeGaps), [filterByReviewStatus, timeGaps]);
    const scheduleAnomalies = useMemo(() => {
        const gapRows = filteredTimeGaps.map((row) => ({
            ...row,
            scheduleType: 'gap',
            routeName: row.routeName || '-',
            driver: row.driver || '-',
            timeDisplay: formatTimeRange(row.fromTime, row.toTime),
            detail: formatDetailRange(
                firstText(row.fromLocationName, row.fromMachine),
                firstText(row.toLocationName, row.toMachine),
                `${firstText(row.fromMachine, '-')} → ${firstText(row.toMachine, '-')}`
            ),
            duration: row.gapDisplay || '-',
            severity: numberValue(row.gapMinutes) >= 180 ? 'danger' : 'warning',
            severityLabel: numberValue(row.gapMinutes) >= 180 ? 'Alto' : 'Moderado',
            sortWeight: numberValue(row.gapMinutes)
        }));
        const afterHoursRows = filteredAfterHours.map((row) => {
            const rawSeverity = row.severity || 'Info';
            return {
                ...row,
                scheduleType: 'after_hours',
                routeName: row.routeName || '-',
                driver: row.driver || '-',
                timeDisplay: formatTimeRange(row.fromTime, row.toTime, row.serviceTime),
                detail: formatDetailRange(
                    firstText(row.fromLocationName, row.fromMachine),
                    firstText(row.toLocationName, row.toMachine),
                    `${firstText(row.machineName, '-')} — ${firstText(row.locationName, '-')}`
                ),
                duration: '-',
                severity:
                    rawSeverity === 'Crítico' || rawSeverity === 'Alto'
                        ? 'danger'
                        : rawSeverity === 'Moderado'
                        ? 'warning'
                        : 'info',
                severityLabel: rawSeverity,
                sortWeight: 0
            };
        });
        return [...gapRows, ...afterHoursRows];
    }, [filteredAfterHours, filteredTimeGaps]);

    const reviewCounts = useMemo(() => {
        const counts = { none: 0, draft: 0, submitted: 0, approved: 0, rejected: 0 };
        [...unservicedMachines, ...afterHours, ...timeGaps].forEach((row) => {
            const status = row?.review?.status || 'none';
            counts[status] = (counts[status] || 0) + 1;
        });
        return counts;
    }, [unservicedMachines, afterHours, timeGaps]);

    const okrUpdatesDue = useMemo(
        () => (okrWeekly?.results || []).filter((result) => getSubmissionState(result).canSubmit).length,
        [getSubmissionState, okrWeekly]
    );
    const trendRows = useMemo(
        () => (Array.isArray(okrWeekly?.trend) ? okrWeekly.trend : []),
        [okrWeekly]
    );
    const hasOkrTrendData = useMemo(
        () => trendRows.some((row) => row.weeklyEarnedPoints !== null && numberValue(row.weeklyEarnedPoints) > 0),
        [trendRows]
    );
    const trendChartData = useMemo(() => ({
        labels: trendRows.map((row) => `${t('Week')} ${row.weekNumber}`),
        datasets: [
            {
                type: 'bar',
                label: t('Points achieved'),
                data: trendRows.map((row) => row.weeklyEarnedPoints),
                backgroundColor: trendRows.map((row) =>
                    Number(row.weekNumber) === Number(selectedOkrWeek) ? 'rgba(33, 150, 243, 0.9)' : 'rgba(33, 150, 243, 0.45)'
                ),
                borderColor: trendRows.map((row) =>
                    row.state === 'current' ? 'rgb(245, 158, 11)' : 'rgba(33, 150, 243, 0.8)'
                ),
                borderWidth: trendRows.map((row) => row.state === 'current' || Number(row.weekNumber) === Number(selectedOkrWeek) ? 2 : 1),
                borderRadius: 6
            },
            {
                type: 'line',
                label: t('Weekly target'),
                data: trendRows.map((row) => row.weeklyExpectedPoints),
                borderColor: 'rgb(245, 158, 11)',
                backgroundColor: 'rgb(245, 158, 11)',
                borderDash: [6, 5],
                borderWidth: 2,
                pointRadius: 0,
                tension: 0.2
            }
        ]
    }), [selectedOkrWeek, t, trendRows]);
    const trendChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 10 } },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${formatNumber(context.parsed.y)} pts`
                }
            }
        },
        scales: {
            y: { beginAtZero: true, title: { display: true, text: t('Points') } },
            x: { grid: { display: false } }
        }
    }), [t]);

    const getStatusDescription = (status) => {
        if (status === 'draft') return t('Weekly update has not been sent.');
        if (status === 'submitted') return t('Weekly update is pending approval.');
        if (status === 'approved') return t('Weekly update was approved.');
        if (status === 'rejected') return t('Weekly update was rejected.');
        return t('No weekly record');
    };

    const openReviewDialog = (anomaly) => {
        setSelectedAnomaly(anomaly);
        setReviewExplanation(anomaly?.review?.explanation || '');
        setReviewError(null);
        setReviewDialogVisible(true);
    };

    const closeReviewDialog = () => {
        setReviewDialogVisible(false);
        setSelectedAnomaly(null);
        setReviewExplanation('');
        setReviewError(null);
    };

    const buildReviewPayload = () => ({
        anomalyKey: selectedAnomaly?.anomalyKey,
        collaboratorId: selectedCollaboratorId,
        from: formatDateForApi(fromDate),
        to: formatDateForApi(toDate),
        explanation: reviewExplanation
    });

    const handleSaveAndSubmitReview = async () => {
        if (!selectedAnomaly) return;
        if (!reviewExplanation.trim()) {
            setReviewError(new Error(t('Explanation is required to submit.')));
            return;
        }
        setReviewSaving(true);
        setReviewError(null);
        try {
            await saveAndSubmitOperatorAnomalyReview(buildReviewPayload());
            closeReviewDialog();
            await loadSummary();
            await loadPendingReviews();
        } catch (error) {
            setReviewError(error);
        } finally {
            setReviewSaving(false);
        }
    };

    const canEditAnomalyReview = useCallback((row) => {
        const status = row?.review?.status || 'none';
        return canEditSelectedAnomalies && status !== 'submitted' && status !== 'approved';
    }, [canEditSelectedAnomalies]);

    const canOpenAnomalyReview = useCallback((row) =>
        canEditAnomalyReview(row) || Boolean(row?.review),
        [canEditAnomalyReview]
    );

    const reviewStatusBody = (row) => {
        const status = row?.review?.status || 'none';
        const tag = <Tag value={getReviewStatusLabel(status)} severity={getReviewSeverity(status)} />;
        if (!canOpenAnomalyReview(row)) return tag;

        return (
            <button
                type="button"
                className="operator-dashboard-status-trigger"
                onClick={() => openReviewDialog(row)}
                aria-label={`${getReviewStatusLabel(status)} - ${canEditAnomalyReview(row) ? t('Justify') : t('View comment')}`}
            >
                {tag}
            </button>
        );
    };

    const reviewActionBody = (row) => {
        const status = row?.review?.status || 'none';
        const editable = canEditAnomalyReview(row);
        if (status === 'none' && !editable) {
            return <span className="text-color-secondary">-</span>;
        }
        const label = editable ? t('Justify') : t('View comment');
        return (
            <Button
                icon={editable ? 'pi pi-pencil' : 'pi pi-eye'}
                label={label}
                className="p-button-sm p-button-text"
                onClick={() => openReviewDialog(row)}
            />
        );
    };

    const scheduleAnomalyTypeBody = (row) => (
        <Tag
            value={row.scheduleType === 'gap' ? t('Gap') : t('After Hours')}
            severity={row.scheduleType === 'gap' ? 'warning' : 'danger'}
        />
    );

    const scheduleAnomalyDurationBody = (row) =>
        row.duration && row.duration !== '-' ? <Tag value={row.duration} severity="warning" /> : '-';

    const scheduleAnomalySeverityBody = (row) => (
        <Tag value={row.severityLabel || '-'} severity={row.severity || 'info'} />
    );

    const getAnomalyTypeLabel = (type) => {
        if (type === 'after_hours') return t('After hours');
        if (type === 'time_gap') return t('Time gaps');
        return t('Unserviced machines');
    };

    const openDecisionDialog = (action, review) => {
        setDecisionDialog({ visible: true, action, review });
        setDecisionComment('');
        setReviewError(null);
    };

    const closeDecisionDialog = () => {
        setDecisionDialog({ visible: false, action: '', review: null });
        setDecisionComment('');
    };

    const handleDecision = async () => {
        if (!decisionDialog.review) return;
        if (decisionDialog.action === 'reject' && !decisionComment.trim()) {
            setReviewError(new Error(t('Review comment is required to reject.')));
            return;
        }
        setDecisionSaving(true);
        setReviewError(null);
        try {
            if (decisionDialog.action === 'approve') {
                await approveOperatorAnomalyReview(decisionDialog.review.id, { reviewComment: decisionComment });
            } else {
                await rejectOperatorAnomalyReview(decisionDialog.review.id, { reviewComment: decisionComment });
            }
            closeDecisionDialog();
            await loadPendingReviews();
            if (summary) await loadSummary();
        } catch (error) {
            setReviewError(error);
        } finally {
            setDecisionSaving(false);
        }
    };

    const closeReturnDialog = () => {
        if (returnSaving) return;
        setReturnDialog({ visible: false, ticket: null });
        setReturnNote('');
    };

    const handleCompleteProductReturn = async () => {
        if (!returnDialog.ticket || !selectedCollaboratorId) return;
        setReturnSaving(true);
        setReturnTicketsError(null);
        try {
            await completeOperatorProductReturn(returnDialog.ticket.id, {
                collaboratorId: selectedCollaboratorId,
                note: returnNote.trim()
            });
            setReturnDialog({ visible: false, ticket: null });
            setReturnNote('');
            await loadReturnTickets();
        } catch (error) {
            setReturnTicketsError(error);
        } finally {
            setReturnSaving(false);
        }
    };

    const closeSupportReturnDialog = () => {
        if (supportReturnSaving) return;
        setSupportReturnDialog({ visible: false, ticket: null });
        setSupportReturnReason('');
    };

    const handleReturnProductToSupport = async () => {
        if (!supportReturnDialog.ticket || !selectedCollaboratorId || !supportReturnReason.trim()) return;
        setSupportReturnSaving(true);
        setReturnTicketsError(null);
        try {
            await returnOperatorProductToSupport(supportReturnDialog.ticket.id, {
                collaboratorId: selectedCollaboratorId,
                reason: supportReturnReason.trim()
            });
            setSupportReturnDialog({ visible: false, ticket: null });
            setSupportReturnReason('');
            await loadReturnTickets();
        } catch (error) {
            setReturnTicketsError(error);
        } finally {
            setSupportReturnSaving(false);
        }
    };

    const getReviewSubject = (review = {}) => {
        const source = review.sourceSnapshot || {};
        if (review.anomalyType === 'time_gap') {
            const from = source.fromMachine || source.from_machine || '-';
            const to = source.toMachine || source.to_machine || '-';
            const fromTime = source.fromTime || source.from_time;
            const toTime = source.toTime || source.to_time;
            const timeRange = fromTime && toTime ? ` (${fromTime} - ${toTime})` : '';
            return `${from} -> ${to}${timeRange}`;
        }
        return review.machineNameSnapshot || source.machineName || source.machine_name || '-';
    };

    const selectedReviewStatus = selectedAnomaly?.review?.status || 'none';
    const reviewEditable = canEditSelectedAnomalies && selectedReviewStatus !== 'submitted' && selectedReviewStatus !== 'approved';
    const pendingApprovalsHeader = `${t('Anomaly approvals')} (${pendingReviews.length})`;
    const returnMethodBody = (row) => (
        <Tag
            value={t(row.resolutionMethod === 'bank_refund' ? 'Bank refund' : 'Product delivery')}
            severity={row.resolutionMethod === 'bank_refund' ? 'info' : 'warning'}
            icon={row.resolutionMethod === 'bank_refund' ? 'pi pi-wallet' : 'pi pi-box'}
        />
    );
    const returnStatusBody = (row) => {
        if (row.resolutionMethod === 'bank_refund') {
            const statusLabels = {
                draft: 'Preparing refund',
                pending_finance_review: 'Pending Finance review',
                ready_for_bank_upload: 'Ready for bank',
                revision: 'Under revision',
                exported: 'Exported to bank file',
                submitted_to_bank: 'Submitted to bank',
                rejected: 'Refund rejected'
            };
            return <Tag value={t(statusLabels[row.refundStatus] || 'Pending Finance')} severity={row.refundStatus === 'rejected' ? 'danger' : 'info'} />;
        }
        return <Tag value={t('Pending product delivery')} severity="warning" />;
    };
    const returnMachineBody = (row) => (
        <div className="operator-dashboard-ticket-machine">
            <strong>{row.machineName || row.machineCode || '-'}</strong>
            <small>{row.machineCode || '-'}</small>
        </div>
    );
    const returnActionBody = (row) => row.canComplete ? (
        <div className="flex gap-2 flex-wrap">
            <Button
                icon="pi pi-check-circle"
                label={t('Product delivered')}
                className="p-button-sm p-button-success"
                onClick={() => {
                    setReturnDialog({ visible: true, ticket: row });
                    setReturnNote('');
                }}
            />
            {row.canReturnToSupport ? (
                <Button
                    icon="pi pi-replay"
                    label={t('Could not deliver')}
                    className="p-button-sm p-button-outlined p-button-warning"
                    onClick={() => {
                        setSupportReturnDialog({ visible: true, ticket: row });
                        setSupportReturnReason('');
                    }}
                />
            ) : null}
        </div>
    ) : (
        <span className="text-color-secondary">{t('Managed by Finance')}</span>
    );
    const attentionItems = [
        {
            key: 'unserviced',
            label: t('Unserviced machines KPI'),
            value: numberValue(routeExecution.unserviced),
            icon: 'pi pi-exclamation-triangle',
            onClick: () => {
                setMainTabIndex(1);
                setAnomalyTabIndex(0);
            }
        },
        {
            key: 'anomalies',
            label: t('Unjustified anomalies'),
            value: reviewCounts.none,
            icon: 'pi pi-exclamation-circle',
            onClick: () => setMainTabIndex(1)
        },
        {
            key: 'returns',
            label: t('Pending returns'),
            value: returnTickets.total,
            icon: 'pi pi-replay',
            onClick: () => setMainTabIndex(3)
        },
        {
            key: 'okr',
            label: t('OKR updates to send'),
            value: okrUpdatesDue,
            icon: 'pi pi-send',
            onClick: () => {
                setMainTabIndex(0);
                window.requestAnimationFrame(() => document.getElementById('operator-dashboard-okrs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
            }
        }
    ];
    const hasAttentionItems = attentionItems.some((item) => item.value > 0);

    return (
        <div className="operator-dashboard-page">
            <div className="operator-dashboard-header">
                <h2>{t('Operator dashboard title')}</h2>
            </div>

            {canViewSupervision && dashboardAccess ? (
                <div className="operator-supervision-view-tabs" role="tablist" aria-label="Vista del dashboard">
                    {dashboardAccess ? <Button role="tab" aria-selected={!coffeeActive && dashboardView === 'individual'} label="Operación individual"
                        className={!coffeeActive && dashboardView === 'individual' ? '' : 'p-button-outlined'} onClick={() => openDashboardView('individual')} /> : null}
                    {canViewSupervision && dashboardAccess ? <Button role="tab" aria-selected={!coffeeActive && dashboardView === 'supervision'} label="Supervisión"
                        className={!coffeeActive && dashboardView === 'supervision' ? '' : 'p-button-outlined'} onClick={() => openDashboardView('supervision')} /> : null}
                </div>
            ) : null}
            {dashboardView === 'supervision' && canViewSupervision ? (
                <OperatorSupervision onOpenOperator={(operatorId, week) => {
                    setSelectedCollaboratorId(operatorId);
                    if (week) { setSelectedOkrWeek(week); applyOkrWeekDates(week, okrContext?.weeks || []); }
                    setDashboardView('individual');
                }} />
            ) : <>
            {!coffeeActive && dashboardAccess ? <>
            <Card className="operator-dashboard-card operator-dashboard-filters">
                <>
                        {okrError ? (
                            <Message
                                severity={okrError.status === 403 ? 'warn' : 'error'}
                                text={okrError.status === 403 ? t('You do not have access to this module.') : okrError.message || t('Could not load OKR weekly results.')}
                            />
                        ) : null}
                        <div className="operator-dashboard-filter-grid">
                            <div className="operator-dashboard-field">
                                <label>{t('Operator')}</label>
                                {canAccessAllOperators ? (
                                    <Dropdown
                                        value={selectedCollaboratorId}
                                        options={collaboratorOptions}
                                        onChange={(event) => {
                                            setSelectedCollaboratorId(event.value || '');
                                            setSummary(null);
                                            setOkrWeekly(null);
                                        }}
                                        placeholder={t('Select an operator')}
                                        className="w-full"
                                        disabled={okrLoading || !collaboratorOptions.length}
                                        filter
                                    />
                                ) : (
                                    <div className="operator-dashboard-readonly-field">
                                        {selectedCollaborator?.name || selectedCollaborator?.email || t('Operator')}
                                    </div>
                                )}
                            </div>
                            <div className="operator-dashboard-field">
                                <label>{t('OKR week')}</label>
                                <Dropdown
                                    value={selectedOkrWeek}
                                    options={okrWeekOptions}
                                    onChange={(event) => {
                                        setSelectedOkrWeek(event.value);
                                        applyOkrWeekDates(event.value, okrContext?.weeks || []);
                                    }}
                                    placeholder={t('Current week')}
                                    className="w-full"
                                    disabled={okrLoading || !okrWeekOptions.length}
                                />
                            </div>
                            <div className="operator-dashboard-field">
                                <label>{t('Operator dashboard period')}</label>
                                <div className="operator-dashboard-readonly-field">
                                    {formatDateForApi(fromDate) || '-'} - {formatDateForApi(toDate) || '-'}
                                </div>
                            </div>
                        </div>
                    </>
            </Card>

            {renderSummaryError()}

            {!selectedCollaboratorId ? (
                <Message severity="info" text={t('Select an operator to view their operation and weekly OKR results.')} />
            ) : null}

            {selectedCollaboratorId && summary?.connectionStatus && summary.connectionStatus !== 'matched' ? (
                <Message severity="warn" text={t('No linked Parlevel identity was found for this operator.')} />
            ) : null}

            {summaryLoading ? (
                <div className="operator-dashboard-loading">
                    <ProgressSpinner />
                    <span>{t('Loading summary')}</span>
                </div>
            ) : null}

            {!summaryLoading && summary && !hasOperationalData ? (
                <Message severity="info" text={t('No operational data was found for this operator and period.')} />
            ) : null}

            {!summaryLoading && summary?.routeExecution?.sourceStatus?.message ? (
                <Message severity="info" text={t(summary.routeExecution.sourceStatus.message)} />
            ) : null}

            {summary && okrWeekly && selectedCollaboratorId && !returnTicketsLoading && !returnTicketsError ? (
                hasAttentionItems ? (
                    <section className="operator-dashboard-attention" aria-label={t('Needs attention')}>
                        {attentionItems.filter((item) => item.value > 0).map((item) => (
                            <button type="button" className="operator-dashboard-attention-item" onClick={item.onClick} key={item.key}>
                                <i className={item.icon} aria-hidden="true"></i>
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </button>
                        ))}
                    </section>
                ) : (
                    <div className="operator-dashboard-all-clear" role="status">
                        <i className="pi pi-check-circle" aria-hidden="true"></i>
                        <span>{t('Everything is up to date')}</span>
                    </div>
                )
            ) : null}

            </> : null}
            {(summary || canReviewAnomalies || canViewCoffee) ? (
                <TabView
                    className="operator-dashboard-main-tabs"
                    activeIndex={mainTabIndex}
                    onTabChange={(event) => {
                        setMainTabIndex(event.index);
                        const params = new URLSearchParams(location.search);
                        if (canViewCoffee && event.index === coffeeTabIndex) params.set("tab", "coffee");
                        else if (event.index === 1) params.set("tab", "anomalies");
                        else if (event.index === 3) params.set("tab", "returns");
                        else params.delete("tab");
                        navigate({ pathname: location.pathname, search: params.toString() });
                    }}
                >
                    {dashboardAccess ? <TabPanel header={t('Summary')}>
                        {summary ? (
                            <>
                    <section className="operator-dashboard-section">
                        <h3>{t('Operator operation')}</h3>
                        <div className="operator-dashboard-operation-grid">
                            <Card className={`operator-dashboard-card operator-dashboard-metric-card ${numberValue(routeExecution.unserviced) > 0 ? 'has-warning' : ''}`}>
                                <div className="operator-dashboard-metric-heading">
                                    <span>{t('Compliance')}</span>
                                    <span className="operator-dashboard-metric-okr-label">{t('OKR 1')}</span>
                                </div>
                                <strong>{formatNumber(routeExecution.complianceRate)}%</strong>
                                <ProgressBar value={clampProgressPercentage(routeExecution.complianceRate)} showValue={false} />
                                <div className="operator-dashboard-services-summary">
                                    <span className="operator-dashboard-metric-detail">{t('Services')}</span>
                                    <strong>{formatNumber(routeExecution.serviced)} / {formatNumber(routeExecution.planned)}</strong>
                                    <span className="operator-dashboard-metric-detail">{t('Serviced')} / {t('Scheduled')}</span>
                                    <span className="operator-dashboard-warning-detail">
                                        {t('Unserviced machines KPI')}: {formatNumber(routeExecution.unserviced)}
                                    </span>
                                </div>
                            </Card>
                            {todayExecution ? (
                                <Card className="operator-dashboard-card operator-dashboard-metric-card is-today">
                                    <div className="operator-dashboard-metric-heading">
                                        <span>{t('Services today')}</span>
                                        <i className="pi pi-clock" aria-hidden="true"></i>
                                    </div>
                                    <strong>{formatNumber(todayExecution.serviced)} / {formatNumber(todayExecution.planned)}</strong>
                                    <span className="operator-dashboard-metric-detail">{t('Completed services')} / {t('Scheduled')}</span>
                                    <span className="operator-dashboard-today-detail">
                                        {t('Pending today')}: {formatNumber(todayExecution.pending)}
                                    </span>
                                    <small>{t('Today is still in progress; pending services are not anomalies.')}</small>
                                </Card>
                            ) : null}
                            <Card className="operator-dashboard-card operator-dashboard-metric-card">
                                <div className="operator-dashboard-metric-heading">
                                    <span>{t('Completed maintenance')}</span>
                                    <span className="operator-dashboard-metric-okr-label">{t('OKR 2')}</span>
                                </div>
                                <strong>{formatNumber(completedMaintenance)}</strong>
                            </Card>
                        </div>
                    </section>

                    <section className="operator-dashboard-section operator-dashboard-okr-results-section">
                        <h3>{t('Weekly OKR results')}</h3>
                        {okrLoading || okrWeeklyLoading ? (
                            <div className="operator-dashboard-loading-inline">
                                <ProgressSpinner style={{ width: '28px', height: '28px' }} strokeWidth="4" />
                                <span>{t('Loading OKR results')}</span>
                            </div>
                        ) : null}
                        {!okrLoading && !selectedCollaboratorId ? (
                            <Message severity="info" text={t('Select an operator to view their weekly OKR results.')} />
                        ) : null}
                        {!okrLoading && okrContext?.status === 'empty' ? (
                            <Message severity="warn" text={t('Operations OKR context is not available.')} />
                        ) : null}
                        {okrWeekly ? (
                            <>
                                <div className="operator-dashboard-okr-summary">
                                    <Card className="operator-dashboard-card operator-dashboard-progress-card">
                                        <div className="operator-dashboard-progress-heading">
                                            <span>{t('Weekly progress')}</span>
                                            <strong>{formatNumber(okrWeekly.summary?.weeklyAchievementPercentage)}%</strong>
                                        </div>
                                        <div className="operator-dashboard-progress-points">
                                            {formatNumber(okrWeekly.summary?.weeklyEarnedPoints)} / {formatNumber(okrWeekly.summary?.weeklyExpectedPoints)} pts
                                        </div>
                                        <ProgressBar value={clampProgressPercentage(okrWeekly.summary?.weeklyAchievementPercentage)} showValue={false} />
                                    </Card>
                                    <Card className="operator-dashboard-card operator-dashboard-progress-card">
                                        <div className="operator-dashboard-progress-heading">
                                            <span>{t('Cycle progress')}</span>
                                            <strong>{formatNumber(okrWeekly.summary?.cycleProgressPercentage)}%</strong>
                                        </div>
                                        <div className="operator-dashboard-progress-points">
                                            {formatNumber(okrWeekly.summary?.cycleAccumulatedPoints)} / {formatNumber(okrWeekly.summary?.assignedPoints)} pts
                                        </div>
                                        <ProgressBar value={clampProgressPercentage(okrWeekly.summary?.cycleProgressPercentage)} showValue={false} />
                                    </Card>
                                </div>

                                <Card className="operator-dashboard-card operator-dashboard-trend-card">
                                    <div className="operator-dashboard-section-heading">
                                        <div>
                                            <h3>{t('Cycle trend')}</h3>
                                            <span>{t('Points achieved compared with the weekly target')}</span>
                                        </div>
                                    </div>
                                    {hasOkrTrendData ? (
                                        <>
                                            <div className="operator-dashboard-trend-chart">
                                                <Chart type="bar" data={trendChartData} options={trendChartOptions} />
                                            </div>
                                            <div className="operator-dashboard-sr-only">
                                                {trendRows.map((row) => `${t('Week')} ${row.weekNumber}: ${row.weeklyEarnedPoints === null ? t('Future week') : `${formatNumber(row.weeklyEarnedPoints)} ${t('Points')}`}; ${t('Weekly target')}: ${formatNumber(row.weeklyExpectedPoints)}.`).join(' ')}
                                            </div>
                                        </>
                                    ) : (
                                        <Message severity="info" text={t('No historical OKR points are available for this cycle.')} />
                                    )}
                                </Card>

                                <div id="operator-dashboard-okrs" className="operator-dashboard-score-grid">
                                    {(okrWeekly.results || []).map((result, index) => {
                                        const submissionState = getSubmissionState(result);
                                        const inlineDetails = getOkrInlineDetails(result);
                                        return (
                                            <Card className="operator-dashboard-card operator-dashboard-score-card" key={result.id}>
                                                <div className="operator-dashboard-score-header">
                                                    <h3>{`OKR ${index + 1}`}</h3>
                                                    <Tag
                                                        value={result.statusLabel || result.status}
                                                        severity={getStatusSeverity(result.status)}
                                                        title={getStatusDescription(result.status)}
                                                    />
                                                </div>
                                                <div className="operator-dashboard-score-body">
                                                    <div className="operator-dashboard-score-title">{result.title}</div>
                                                    <div className="operator-dashboard-score-measurement">
                                                        <span>{t('Measurement')}</span>
                                                        <p>{result.measurement || '-'}</p>
                                                    </div>
                                                    <div className="operator-dashboard-score-meta">{t('Weight')}: {formatNumber(result.weight)} pts</div>
                                                    <div className="operator-dashboard-score-details">
                                                        <span className="operator-dashboard-score-details-title">{t('Calculation details')}</span>
                                                        {inlineDetails.length ? (
                                                            <div className="operator-dashboard-score-details-grid">
                                                                {inlineDetails.map((detail) => (
                                                                    <div className="operator-dashboard-score-detail" key={detail.label}>
                                                                        <span>{t(detail.label)}</span>
                                                                        <strong>{formatNumber(detail.value)}{detail.suffix || ''}</strong>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <small>{result.sourceDetailsError || t('Calculation details are not available for this week.')}</small>
                                                        )}
                                                    </div>
                                                    <div className="operator-dashboard-score-progress-heading">
                                                        <span>{formatNumber(result.weeklyEarnedPoints)} / {formatNumber(result.weeklyExpectedPoints)} pts</span>
                                                        <strong>{formatNumber(result.weeklyAchievementPercentage)}%</strong>
                                                    </div>
                                                    <ProgressBar value={clampProgressPercentage(result.weeklyAchievementPercentage)} showValue={false} />
                                                    <div className="operator-dashboard-score-action">
                                                        {submissionState.canSubmit ? (
                                                            <Button
                                                                icon="pi pi-send"
                                                                label={t('Send update')}
                                                                className="p-button-sm"
                                                                onClick={() => handleSubmitOkrUpdate(result)}
                                                                loading={submittingEntryId === result.entryId}
                                                                disabled={Boolean(submittingEntryId) && submittingEntryId !== result.entryId}
                                                            />
                                                        ) : (
                                                            <span className="operator-dashboard-unavailable-reason">
                                                                <i className="pi pi-info-circle" aria-hidden="true"></i>
                                                                {t(submissionState.reason)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </>
                        ) : null}
                    </section>

                            </>
                        ) : (
                            <Message severity="info" text={t('Select an operator to view their operation and weekly OKR results.')} />
                        )}
                    </TabPanel> : null}
                    {dashboardAccess ? <TabPanel header={`${t('Anomalies')} (${reviewCounts.none})`}>
                        <div className="operator-dashboard-anomalies-tab">
                            {summary ? (
                                <section className="operator-dashboard-section">
                                    <h3>{t('Anomalies')}: {selectedCollaborator?.name || summary?.collaborator?.name || t('Operator')}</h3>
                                    {canReviewAnomalies ? (
                                        <div className={`operator-dashboard-reviewer-summary ${reviewCounts.none > 0 ? 'has-alerts' : 'is-clear'}`}>
                                            <div className="operator-dashboard-reviewer-summary-icon" aria-hidden="true">
                                                <i className={reviewCounts.none > 0 ? 'pi pi-exclamation-circle' : 'pi pi-check-circle'}></i>
                                            </div>
                                            <div className="operator-dashboard-reviewer-summary-copy">
                                                <span>{t('Selected operator summary')}</span>
                                                <strong>
                                                    {selectedCollaborator?.name || summary?.collaborator?.name || t('Operator')}{' '}
                                                    {reviewCounts.none > 0
                                                        ? `${t('has')} ${reviewCounts.none} ${t(reviewCounts.none === 1 ? 'unjustified anomaly' : 'unjustified anomalies')} ${t('in this period.')}`
                                                        : t('has no unjustified anomalies in this period.')}
                                                </strong>
                                            </div>
                                            <Tag
                                                value={reviewCounts.none > 0 ? t('Needs attention') : t('Up to date')}
                                                severity={reviewCounts.none > 0 ? 'warning' : 'success'}
                                            />
                                        </div>
                                    ) : null}
                                    <div className="operator-dashboard-review-toolbar">
                                        <div className="operator-dashboard-review-counts">
                                            <Tag value={`${t('Unjustified')}: ${reviewCounts.none}`} severity="secondary" />
                                            <Tag value={`${t('Draft')}: ${reviewCounts.draft}`} severity="warning" />
                                            <Tag value={`${t('Pending approval')}: ${reviewCounts.submitted}`} severity="info" />
                                            <Tag value={`${t('Approved')}: ${reviewCounts.approved}`} severity="success" />
                                            <Tag value={`${t('Rejected')}: ${reviewCounts.rejected}`} severity="danger" />
                                        </div>
                                        <div className="operator-dashboard-review-filter">
                                            <label>{t('Justification status')}</label>
                                            <Dropdown
                                                value={reviewStatusFilter}
                                                options={reviewStatusOptions}
                                                onChange={(event) => setReviewStatusFilter(event.value || '')}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                    <TabView
                                        activeIndex={anomalyTabIndex}
                                        onTabChange={(event) => setAnomalyTabIndex(event.index)}
                                    >
                                        <TabPanel header={`${t('Unserviced machines')} (${unservicedMachines.length})`}>
                                            <DataTable value={filteredUnservicedMachines} emptyMessage={t('No anomalies were detected in this period.')} responsiveLayout="scroll">
                                                <Column field="serviceDate" header={t('Operator dashboard date')} />
                                                <Column field="machineName" header={t('Operator dashboard machine')} />
                                                <Column field="serviceTime" header={t('Service time')} body={(row) => row.serviceTime || '-'} />
                                                <Column field="locationName" header={t('Operator dashboard location')} />
                                                <Column field="machineStatus" header={t('Reason/status')} body={(row) => row.machineStatus || t('Not serviced')} />
                                                <Column header={t('Status')} body={reviewStatusBody} />
                                                <Column header={t('Action')} body={reviewActionBody} />
                                            </DataTable>
                                        </TabPanel>
                                        <TabPanel header={`${t('Schedule Anomalies')} (${afterHours.length + timeGaps.length})`}>
                                            <div className="operator-dashboard-schedule-summary">
                                                <Tag value={`${timeGaps.length} ${t('gaps')}`} severity="warning" />
                                                <Tag value={`${afterHours.length} ${t('after hrs')}`} severity="danger" />
                                            </div>
                                            <DataTable value={scheduleAnomalies} emptyMessage={t('No schedule anomalies detected')} responsiveLayout="scroll" size="small" sortField="routeName" sortOrder={1}>
                                                <Column field="scheduleType" header={t('Type')} sortable body={scheduleAnomalyTypeBody} style={{ width: '105px' }} />
                                                <Column field="routeName" header={t('Route')} sortable style={{ minWidth: '120px' }} />
                                                <Column field="driver" header={t('Driver')} sortable style={{ minWidth: '150px' }} />
                                                <Column field="serviceDate" header={t('Operator dashboard date')} sortable style={{ width: '110px' }} />
                                                <Column field="timeDisplay" header={t('Time')} style={{ width: '130px' }} />
                                                <Column field="detail" header={t('Detail')} style={{ minWidth: '240px' }} />
                                                <Column field="duration" header={t('Duration')} body={scheduleAnomalyDurationBody} style={{ width: '105px', textAlign: 'center' }} />
                                                <Column field="severityLabel" header={t('Severity')} sortable body={scheduleAnomalySeverityBody} style={{ width: '120px', textAlign: 'center' }} />
                                                <Column header={t('Status')} body={reviewStatusBody} />
                                                <Column header={t('Action')} body={reviewActionBody} />
                                            </DataTable>
                                        </TabPanel>
                                    </TabView>
                                </section>
                            ) : (
                                <Message severity="info" text={t('Select an operator to view their anomalies.')} />
                            )}

                            {canReviewAnomalies && selectedCollaboratorId ? (
                                <section className="operator-dashboard-section">
                                    <h3>{pendingApprovalsHeader}</h3>
                                    {pendingReviewsLoading ? (
                                        <div className="operator-dashboard-loading-inline">
                                            <ProgressSpinner style={{ width: '28px', height: '28px' }} strokeWidth="4" />
                                            <span>{t('Loading pending approvals')}</span>
                                        </div>
                                    ) : null}
                                    <DataTable value={pendingReviews} emptyMessage={t('No pending approvals for this operator.')} responsiveLayout="scroll">
                                        <Column field="driverNameSnapshot" header={t('Operator')} />
                                        <Column header={t('Anomaly type')} body={(row) => getAnomalyTypeLabel(row.anomalyType)} />
                                        <Column field="serviceDate" header={t('Operator dashboard date')} />
                                        <Column header={t('Machine or gap')} body={getReviewSubject} />
                                        <Column field="explanation" header={t('Explanation')} />
                                        <Column header={t('Submitted at')} body={(row) => (row.submittedAt ? formatLocalDateTime(new Date(row.submittedAt)) : '-')} />
                                        <Column
                                            header={t('Action')}
                                            body={(row) => (
                                                <div className="operator-dashboard-row-actions">
                                                    <Button
                                                        icon="pi pi-check"
                                                        label={t('Approve')}
                                                        className="p-button-sm p-button-success"
                                                        onClick={() => openDecisionDialog('approve', row)}
                                                    />
                                                    <Button
                                                        icon="pi pi-times"
                                                        label={t('Reject')}
                                                        className="p-button-sm p-button-danger p-button-outlined"
                                                        onClick={() => openDecisionDialog('reject', row)}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </DataTable>
                                </section>
                            ) : null}
                        </div>
                    </TabPanel> : null}
                    {canViewCoffee ? <TabPanel header="Estadisticas Cafe"><CoffeeRegistration user={currentAdminUser} /></TabPanel> : null}
                    {returnTickets.productDeliveries > 0 && dashboardAccess ? (
                    <TabPanel header={`${t('Pending returns')} (${returnTickets.productDeliveries})`}>
                        <div className="operator-dashboard-returns-tab">
                            {selectedCollaboratorId ? (
                                <section className="operator-dashboard-section">
                                    <div className="operator-dashboard-section-heading">
                                        <div>
                                            <h3>{t('Pending returns')}: {selectedCollaborator?.name || summary?.collaborator?.name || t('Operator')}</h3>
                                            <span>{t('Pending resolutions for machines assigned to this operator')}</span>
                                        </div>
                                        <div className="operator-dashboard-review-counts">
                                            <Tag
                                                value={`${t('Product deliveries')}: ${returnTickets.productDeliveries}`}
                                                severity="warning"
                                                icon="pi pi-box"
                                            />
                                        </div>
                                    </div>
                                    <div className="operator-dashboard-return-guidance">
                                        <div>
                                            <i className="pi pi-box"></i>
                                            <span>{t('Only legacy product deliveries already assigned to this operator appear here.')}</span>
                                        </div>
                                    </div>
                                    {returnTicketsLoading ? (
                                        <div className="operator-dashboard-loading-inline">
                                            <ProgressSpinner style={{ width: '28px', height: '28px' }} strokeWidth="4" />
                                            <span>{t('Loading returns')}</span>
                                        </div>
                                    ) : null}
                                    {returnTicketsError && !returnDialog.visible ? (
                                        <Message severity="warn" text={returnTicketsError.message || t('Could not load returns.')} />
                                    ) : null}
                                    {!returnTicketsLoading ? (
                                        <DataTable
                                            value={returnTickets.items}
                                            emptyMessage={t('This operator has no pending returns.')}
                                            responsiveLayout="scroll"
                                            paginator={returnTickets.items.length > 10}
                                            rows={10}
                                        >
                                            <Column field="ticketCode" header={t('Ticket')} style={{ width: '100px' }} />
                                            <Column header={t('Resolution')} body={returnMethodBody} style={{ minWidth: '160px' }} />
                                            <Column header={t('Machine')} body={returnMachineBody} style={{ minWidth: '180px' }} />
                                            <Column field="locationName" header={t('Location')} body={(row) => row.locationName || '-'} style={{ minWidth: '180px' }} />
                                            <Column field="routeName" header={t('Route')} body={(row) => row.routeName || '-'} />
                                            <Column header={t('Customer')} body={(row) => row.customerName || row.customerPhone || '-'} style={{ minWidth: '150px' }} />
                                            <Column field="issueSummary" header={t('Ticket detail')} body={(row) => row.issueSummary || '-'} style={{ minWidth: '220px' }} />
                                            <Column field="supportNote" header={t('Office instructions')} body={(row) => row.supportNote || '-'} style={{ minWidth: '200px' }} />
                                            <Column header={t('Created')} body={(row) => row.createdAt ? formatLocalDateTime(new Date(row.createdAt)) : '-'} />
                                            <Column header={t('Age')} body={(row) => `${numberValue(row.ageDays)} ${t('days')}`} />
                                            <Column header={t('Status')} body={returnStatusBody} style={{ minWidth: '180px' }} />
                                            <Column header={t('Action')} body={returnActionBody} style={{ minWidth: '210px' }} />
                                        </DataTable>
                                    ) : null}
                                </section>
                            ) : (
                                <Message severity="info" text={t('Select an operator to view their pending returns.')} />
                            )}
                        </div>
                    </TabPanel>
                    ) : null}
                </TabView>
            ) : null}

            <Dialog
                header={t('Confirm product delivery')}
                visible={returnDialog.visible}
                onHide={closeReturnDialog}
                className="operator-dashboard-return-dialog"
                modal
                footer={
                    <div className="operator-dashboard-dialog-actions">
                        <Button label={t('Cancel')} className="p-button-text" onClick={closeReturnDialog} disabled={returnSaving} />
                        <Button
                            label={t('Confirm delivery')}
                            icon="pi pi-check-circle"
                            className="p-button-success"
                            onClick={handleCompleteProductReturn}
                            loading={returnSaving}
                        />
                    </div>
                }
            >
                {returnDialog.ticket ? (
                    <div className="operator-dashboard-review-content">
                        <Message
                            severity="warn"
                            text={t('Confirm only after the product has been delivered. If a refund is pending, the ticket remains open for Finance.')}
                        />
                        <div className="operator-dashboard-review-detail-grid">
                            <div><strong>{t('Ticket')}:</strong> {returnDialog.ticket.ticketCode}</div>
                            <div><strong>{t('Machine')}:</strong> {returnDialog.ticket.machineName || returnDialog.ticket.machineCode || '-'}</div>
                            <div><strong>{t('Location')}:</strong> {returnDialog.ticket.locationName || '-'}</div>
                            <div><strong>{t('Customer')}:</strong> {returnDialog.ticket.customerName || returnDialog.ticket.customerPhone || '-'}</div>
                            <div><strong>{t('Product to deliver')}:</strong> {returnDialog.ticket.issueSummary || '-'}</div>
                            <div><strong>{t('Office instructions')}:</strong> {returnDialog.ticket.supportNote || '-'}</div>
                        </div>
                        <div className="operator-dashboard-field">
                            <label>{t('Delivery note')}</label>
                            <InputTextarea
                                value={returnNote}
                                onChange={(event) => setReturnNote(event.target.value)}
                                rows={4}
                                maxLength={1000}
                                disabled={returnSaving}
                                className="w-full"
                                placeholder={t('Optional: product delivered, customer contacted, or relevant detail')}
                            />
                            <small className="text-color-secondary">{returnNote.length}/1000</small>
                        </div>
                        {returnTicketsError ? (
                            <Message severity="error" text={returnTicketsError.message || t('Could not complete the return.')} />
                        ) : null}
                    </div>
                ) : null}
            </Dialog>

            <Dialog
                header={t('Return to Customer Service')}
                visible={supportReturnDialog.visible}
                onHide={closeSupportReturnDialog}
                className="operator-dashboard-return-dialog"
                modal
                footer={
                    <div className="operator-dashboard-dialog-actions">
                        <Button label={t('Cancel')} className="p-button-text" onClick={closeSupportReturnDialog} disabled={supportReturnSaving} />
                        <Button
                            label={t('Send to Customer Service')}
                            icon="pi pi-replay"
                            className="p-button-warning"
                            onClick={handleReturnProductToSupport}
                            loading={supportReturnSaving}
                            disabled={!supportReturnReason.trim()}
                        />
                    </div>
                }
            >
                {supportReturnDialog.ticket ? (
                    <div className="operator-dashboard-review-content">
                        <Message
                            severity="info"
                            text={t('Customer Service will continue the same WhatsApp conversation with the customer.')}
                        />
                        <div className="operator-dashboard-review-detail-grid">
                            <div><strong>{t('Ticket')}:</strong> {supportReturnDialog.ticket.ticketCode}</div>
                            <div><strong>{t('Machine')}:</strong> {supportReturnDialog.ticket.machineName || supportReturnDialog.ticket.machineCode || '-'}</div>
                            <div><strong>{t('Location')}:</strong> {supportReturnDialog.ticket.locationName || '-'}</div>
                            <div><strong>{t('Customer')}:</strong> {supportReturnDialog.ticket.customerName || supportReturnDialog.ticket.customerPhone || '-'}</div>
                            <div><strong>{t('Product to deliver')}:</strong> {supportReturnDialog.ticket.issueSummary || '-'}</div>
                            <div><strong>{t('Office instructions')}:</strong> {supportReturnDialog.ticket.supportNote || '-'}</div>
                        </div>
                        <div className="operator-dashboard-field">
                            <label>{t('Why could you not deliver the product?')}</label>
                            <InputTextarea
                                value={supportReturnReason}
                                onChange={(event) => setSupportReturnReason(event.target.value)}
                                rows={4}
                                maxLength={1000}
                                disabled={supportReturnSaving}
                                className="w-full"
                                placeholder={t('Example: customer was not at the location or could not be reached')}
                            />
                            <small className="text-color-secondary">{supportReturnReason.length}/1000</small>
                        </div>
                        {returnTicketsError ? (
                            <Message severity="error" text={returnTicketsError.message || t('Could not return the task to Customer Service.')} />
                        ) : null}
                    </div>
                ) : null}
            </Dialog>

            <Dialog
                header={reviewEditable ? t('Justify anomaly') : t('View justification')}
                visible={reviewDialogVisible}
                onHide={closeReviewDialog}
                className="operator-dashboard-review-dialog"
                modal
                footer={
                    <div className="operator-dashboard-dialog-actions">
                        <Button label={t('Close')} className="p-button-text" onClick={closeReviewDialog} disabled={reviewSaving} />
                        {reviewEditable ? (
                            <Button
                                label={t('Save and send for approval')}
                                icon="pi pi-send"
                                onClick={handleSaveAndSubmitReview}
                                loading={reviewSaving}
                            />
                        ) : null}
                    </div>
                }
            >
                {selectedAnomaly ? (
                    <div className="operator-dashboard-review-content">
                        <div className="operator-dashboard-review-detail-grid">
                            <div><strong>{t('Anomaly type')}:</strong> {getAnomalyTypeLabel(selectedAnomaly.anomalyType)}</div>
                            <div><strong>{t('Operator')}:</strong> {selectedCollaborator?.name || summary?.collaborator?.name || '-'}</div>
                            <div><strong>{t('Operator dashboard date')}:</strong> {selectedAnomaly.serviceDate || '-'}</div>
                            <div><strong>{t('Operator dashboard machine')}:</strong> {selectedAnomaly.machineName || selectedAnomaly.fromMachine || '-'}</div>
                            <div><strong>{t('Operator dashboard location')}:</strong> {selectedAnomaly.locationName || '-'}</div>
                            {selectedAnomaly.toMachine ? <div><strong>{t('To machine')}:</strong> {selectedAnomaly.toMachine}</div> : null}
                            {selectedAnomaly.serviceTime ? <div><strong>{t('Service time')}:</strong> {selectedAnomaly.serviceTime}</div> : null}
                            {selectedAnomaly.fromTime && selectedAnomaly.toTime ? <div><strong>{t('Gap range')}:</strong> {selectedAnomaly.fromTime} - {selectedAnomaly.toTime}</div> : null}
                            {selectedAnomaly.gapDisplay ? <div><strong>{t('Gap duration')}:</strong> {selectedAnomaly.gapDisplay}</div> : null}
                        </div>
                        <div className="operator-dashboard-field">
                            <label>{t('Why did this anomaly happen?')}</label>
                            <InputTextarea
                                value={reviewExplanation}
                                onChange={(event) => setReviewExplanation(event.target.value)}
                                rows={5}
                                maxLength={2000}
                                disabled={!reviewEditable || reviewSaving}
                                className="w-full"
                            />
                            <small className="text-color-secondary">{reviewExplanation.length}/2000</small>
                        </div>
                        {selectedAnomaly.review ? (
                            <div className="operator-dashboard-review-history">
                                <div className="font-semibold">{t('History')}</div>
                                {(selectedAnomaly.review.history || []).length ? (
                                    selectedAnomaly.review.history.map((item, index) => (
                                        <div key={`${item.action}-${index}`} className="operator-dashboard-history-row">
                                            <Tag value={getReviewStatusLabel(item.action === 'submitted' ? 'submitted' : item.action === 'approved' ? 'approved' : item.action === 'rejected' ? 'rejected' : 'draft')} severity={getReviewSeverity(item.action === 'approved' ? 'approved' : item.action === 'rejected' ? 'rejected' : item.action === 'submitted' ? 'submitted' : 'draft')} />
                                            <span>{item.at ? formatLocalDateTime(new Date(item.at)) : '-'}</span>
                                            <span>{item.comment || '-'}</span>
                                        </div>
                                    ))
                                ) : (
                                    <span className="text-color-secondary">{t('No history yet.')}</span>
                                )}
                            </div>
                        ) : null}
                        {reviewError ? <Message severity="warn" text={reviewError.message || t('Could not save anomaly review.')} /> : null}
                    </div>
                ) : null}
            </Dialog>

            <Dialog
                header={decisionDialog.action === 'reject' ? t('Reject anomaly explanation') : t('Approve anomaly explanation')}
                visible={decisionDialog.visible}
                onHide={closeDecisionDialog}
                className="operator-dashboard-review-dialog"
                modal
                footer={
                    <div className="operator-dashboard-dialog-actions">
                        <Button label={t('Cancel')} className="p-button-text" onClick={closeDecisionDialog} disabled={decisionSaving} />
                        <Button
                            label={decisionDialog.action === 'reject' ? t('Reject') : t('Approve')}
                            icon={decisionDialog.action === 'reject' ? 'pi pi-times' : 'pi pi-check'}
                            className={decisionDialog.action === 'reject' ? 'p-button-danger' : 'p-button-success'}
                            onClick={handleDecision}
                            loading={decisionSaving}
                        />
                    </div>
                }
            >
                <div className="operator-dashboard-review-content">
                    <div className="operator-dashboard-review-detail-grid">
                        <div><strong>{t('Operator')}:</strong> {decisionDialog.review?.driverNameSnapshot || '-'}</div>
                        <div><strong>{t('Operator dashboard date')}:</strong> {decisionDialog.review?.serviceDate || '-'}</div>
                        <div><strong>{t('Anomaly type')}:</strong> {getAnomalyTypeLabel(decisionDialog.review?.anomalyType)}</div>
                    </div>
                    <div className="operator-dashboard-field">
                        <label>{decisionDialog.action === 'reject' ? t('Review comment required') : t('Review comment')}</label>
                        <InputTextarea
                            value={decisionComment}
                            onChange={(event) => setDecisionComment(event.target.value)}
                            rows={4}
                            maxLength={1000}
                            className="w-full"
                            disabled={decisionSaving}
                        />
                    </div>
                    {reviewError ? <Message severity="warn" text={reviewError.message || t('Could not save review decision.')} /> : null}
                </div>
            </Dialog>
            </>}
        </div>
    );
};

export default OperatorDashboard;
